import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  Box,
  Typography,
  Stack,
  Paper,
  TextField,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import "dayjs/locale/es";
import { PurchaseStatus } from "@/types";
import { fetchFilteredPurchases } from "@/features/payments/paymentsThunks";
import { fetchPaymentItems } from "@/features/options/optionsThunks";
import {
  selectPagination,
  selectPaymentItems,
  selectPaymentsLoading,
} from "@/features/payments/paymentsSelectors";
import { resetFilters } from "@/features/payments/paymentsSlice";

const FiltersPanelSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const paymentItems = useAppSelector(selectPaymentItems);
  const isLoading = useAppSelector(selectPaymentsLoading);
  const pagination = useAppSelector(selectPagination);

  const [correo, setCorreo] = useState<string>("");
  const [concepto, setConcepto] = useState<string>("");
  const [fechaInicio, setFechaInicio] = useState<Dayjs | null>(null);
  const [fechaFin, setFechaFin] = useState<Dayjs | null>(null);
  const [estadoFiltro, setEstadoFiltro] = useState<string>("");

  const handleConceptoChange = (event: SelectChangeEvent) => {
    setConcepto(event.target.value);
  };

  const handleEstadoChange = (event: SelectChangeEvent) => {
    setEstadoFiltro(event.target.value);
  };

  const handleApplyFilters = () => {
    // Normalize dates to start of day (00:00:00) for start date
    const startDate = fechaInicio
      ? fechaInicio.startOf('day').toISOString()
      : null;
    // Normalize dates to end of day (23:59:59.999) for end date
    const endDate = fechaFin
      ? fechaFin.endOf('day').toISOString()
      : null;

    dispatch(
      fetchFilteredPurchases({
        filters: {
          dateRange: {
            start: startDate,
            end: endDate,
          },
          status: estadoFiltro ? (estadoFiltro as PurchaseStatus) : null,
          userId: null,
          userName: correo || null,
          paymentItem: concepto || null,
        },
        pageSize: pagination.pageSize,
      })
    ).catch((error) => {
      console.error(error);
    });
  };
  const handleClearFilters = () => {
    setCorreo("");
    setConcepto("");
    setFechaInicio(null);
    setFechaFin(null);
    setEstadoFiltro("");

    dispatch(resetFilters());

    dispatch(
      fetchFilteredPurchases({
        filters: {
          dateRange: { start: null, end: null },
          status: null,
          userId: null,
          userName: null,
          paymentItem: null,
        },
        pageSize: pagination.pageSize,
      })
    ).catch((error) => {
      console.error("Error al limpiar filtros:", error);
    });
  };

  useEffect(() => {
    dispatch(fetchPaymentItems());
  }, [dispatch]);

  useEffect(() => {
    handleApplyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Paper
      elevation={0}
      sx={{
        pb: { xs: 2, sm: 3, md: 4 },
        border: "1px solid #E0E0E0",
        borderRadius: 2,
        mb: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={550}
        gutterBottom
        sx={{
          textAlign: "right",
          color: "text.secondary",
          fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
          pt: { xs: 1, sm: 1.5, md: 1.5 },
          pr: { xs: 2, sm: 2, md: 2 },
        }}
      >
        Filtros
      </Typography>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 2, sm: 2.5, md: 3 }}
        sx={{
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          mt: { xs: 1, sm: 1.5, md: 2 },
        }}
        alignItems="stretch"
      >
        {/* First Column: Correo and Fechas */}
        <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ flex: 1 }}>
          {/* Correo */}
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="body2"
              fontWeight={500}
              gutterBottom
              color="grey.500"
              sx={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
                textAlign: "start",
                mb: 0.5,
              }}
            >
              Correo del usuario
            </Typography>
            <TextField
              fullWidth
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: { xs: "40px", sm: "38px", md: "35px" },
                  fontSize: { xs: "0.875rem", md: "0.875rem" },
                },
              }}
            />
          </Box>

          {/* Fechas */}
          <Box sx={{ width: "100%", pt: { xs: 0, sm: 1, md: 1.5 } }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1.5, sm: 2 }}
                alignItems="flex-start"
              >
                <DatePicker
                  label="Fecha inicio"
                  value={fechaInicio}
                  onChange={(newValue) => setFechaInicio(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          height: { xs: "40px", sm: "38px", md: "35px" },
                          fontSize: { xs: "0.875rem", md: "0.875rem" },
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.95rem", md: "1rem" },
                          lineHeight: "1",
                          transform: {
                            xs: "translate(14px, 11px) scale(1)",
                            sm: "translate(14px, 10px) scale(1)",
                            md: "translate(14px, 9px) scale(1)",
                          },
                        },
                        "& .MuiInputLabel-shrink": {
                          fontSize: { xs: "0.95rem", md: "1rem" },
                          transform: "translate(14px, -9px) scale(0.75)",
                        },
                        "& .MuiOutlinedInput-input": {
                          height: { xs: "40px", sm: "38px", md: "35px" },
                          fontSize: { xs: "0.875rem", md: "0.875rem" },
                          boxSizing: "border-box",
                        },
                        "& .MuiInputAdornment-root": {
                          marginLeft: "8px",
                        },
                        "& .MuiIconButton-root": {
                          padding: { xs: "4px", md: "0px" },
                        },
                      },
                    },
                    actionBar: {
                      actions: ["accept"],
                    },
                    toolbar: {
                      hidden: true,
                    },
                    popper: {
                      placement: "bottom-start",
                      disablePortal: true,
                    },
                    desktopPaper: {
                      sx: {
                        boxShadow: 3,
                      },
                    },
                    layout: {
                      sx: {
                        "& .MuiPickersLayout-toolbar": {
                          display: "none",
                        },
                        "& .MuiPickersLayout-contentWrapper": {
                          maxHeight: "280px",
                        },
                        "& .MuiDayCalendar-header": {
                          fontSize: "0.75rem",
                        },
                        "& .MuiPickersDay-root": {
                          fontSize: "0.75rem",
                          width: "32px",
                          height: "32px",
                        },
                        "& .MuiPickersDay-root.Mui-selected": {
                          backgroundColor: "primary.dark",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                          "&:focus": {
                            backgroundColor: "primary.dark",
                          },
                        },
                        "& .MuiPickersDay-root:hover": {
                          backgroundColor: "primary.main",
                        },
                        "& .MuiPickersDay-today": {
                          border: "1px solid",
                          borderColor: "primary.main",
                        },
                      },
                    },
                  }}
                />
                <DatePicker
                  label="Fecha fin"
                  value={fechaFin}
                  onChange={(newValue) => setFechaFin(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          height: { xs: "40px", sm: "38px", md: "35px" },
                          fontSize: { xs: "0.875rem", md: "0.875rem" },
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.95rem", md: "1rem" },
                          lineHeight: "1",
                          transform: {
                            xs: "translate(14px, 11px) scale(1)",
                            sm: "translate(14px, 10px) scale(1)",
                            md: "translate(14px, 9px) scale(1)",
                          },
                        },
                        "& .MuiInputLabel-shrink": {
                          fontSize: { xs: "0.95rem", md: "1rem" },
                          transform: "translate(14px, -9px) scale(0.75)",
                        },
                        "& .MuiOutlinedInput-input": {
                          height: { xs: "40px", sm: "38px", md: "35px" },
                          fontSize: { xs: "0.875rem", md: "0.875rem" },
                          boxSizing: "border-box",
                        },
                        "& .MuiInputAdornment-root": {
                          marginLeft: "8px",
                        },
                        "& .MuiIconButton-root": {
                          padding: { xs: "4px", md: "4px" },
                        },
                      },
                    },
                    actionBar: {
                      actions: ["accept"],
                    },
                    toolbar: {
                      hidden: true,
                    },
                    popper: {
                      placement: "bottom-start",
                      disablePortal: true,
                    },
                    desktopPaper: {
                      sx: {
                        boxShadow: 3,
                      },
                    },
                    layout: {
                      sx: {
                        "& .MuiPickersLayout-toolbar": {
                          display: "none",
                        },
                        "& .MuiPickersLayout-contentWrapper": {
                          maxHeight: "280px",
                        },
                        "& .MuiDayCalendar-header": {
                          fontSize: "0.75rem",
                        },
                        "& .MuiPickersDay-root": {
                          fontSize: "0.75rem",
                          width: "32px",
                          height: "32px",
                        },
                        "& .MuiPickersDay-root.Mui-selected": {
                          backgroundColor: "primary.dark",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                          "&:focus": {
                            backgroundColor: "primary.dark",
                          },
                        },
                        "& .MuiPickersDay-root:hover": {
                          backgroundColor: "primary.main",
                        },
                        "& .MuiPickersDay-today": {
                          border: "1px solid",
                          borderColor: "primary.main",
                        },
                      },
                    },
                  }}
                />
              </Stack>
            </LocalizationProvider>
          </Box>
        </Stack>

        {/* Second Column: Concepto and Estado */}
        <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ flex: 1 }}>
          {/* Concepto */}
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="body2"
              fontWeight={500}
              gutterBottom
              color="grey.500"
              sx={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
                textAlign: "start",
                mb: 0.5,
              }}
            >
              Concepto del pago
            </Typography>
            <FormControl fullWidth>
              <Select
                value={concepto}
                onChange={handleConceptoChange}
                sx={{
                  height: { xs: "40px", sm: "38px", md: "35px" },
                  fontSize: { xs: "0.875rem", md: "0.875rem" },
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                  {paymentItems?.map((item) => {
                  return (
                    <MenuItem key={item.id} value={item.id}>
                      {
                       // @ts-ignore
                       item.name || item.description}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          {/* Estado */}
          <Box sx={{ width: "100%", pt: { xs: 0, sm: 1, md: 1 } }}>
            <Typography
              variant="body2"
              fontWeight={500}
              gutterBottom
              color="grey.500"
              sx={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
                textAlign: "start",
                mb: 0.5,
              }}
            >
              Estado
            </Typography>
            <FormControl fullWidth>
              <Select
                value={estadoFiltro}
                onChange={handleEstadoChange}
                sx={{
                  height: { xs: "40px", sm: "38px", md: "35px" },
                  fontSize: { xs: "0.875rem", md: "0.875rem" },
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value={PurchaseStatus.PENDING}>Pendiente</MenuItem>
                <MenuItem value={PurchaseStatus.COMPLETED}>Completado</MenuItem>
                <MenuItem value={PurchaseStatus.APPROVED}>Aprobado</MenuItem>
                <MenuItem value={PurchaseStatus.FAILED}>Fallido</MenuItem>
                <MenuItem value={PurchaseStatus.REJECTED}>Rechazado</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </Stack>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          mt: 3,
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClearFilters}
          disabled={isLoading}
          sx={{
            height: { xs: "40px", sm: "38px", md: "36px" },
            fontSize: { xs: "0.875rem", md: "0.875rem" },
          }}
        >
          Limpiar Filtros
        </Button>
        <Button
          variant="contained"
          onClick={handleApplyFilters}
          disabled={isLoading}
          sx={{
            height: { xs: "40px", sm: "38px", md: "36px" },
            fontSize: { xs: "0.875rem", md: "0.875rem" },
          }}
        >
          {isLoading ? "Buscando..." : "Aplicar Filtros"}
        </Button>
      </Stack>
    </Paper>
  );
};

export default FiltersPanelSection;
